import React, { useCallback, useEffect, useState } from "react";
import styles from "./index.module.scss";
import { BiCalendar } from "react-icons/bi";
import ReactDatePicker from "@/components/layout/date-picker/date-picker";
import ClickAwayListener from "react-click-away-listener";
import BlessButton from "@/components/layout/button/green-outline-btn";
import { type DateRangeFilter } from "@/types/bridge-scanner.types";
import { useBridgeScannerFiltersStore } from "@/features/bridge-scanner/stores/filters.store";
import dayjs from "dayjs";
import { FaArrowRightLong } from "react-icons/fa6";
import { useBridgeScannerStore } from "@/features/bridge-scanner/stores/bridge-scanner.store";
import { useFilterApply } from "@/features/bridge-scanner/hooks/useFilterApply.hook";

type DateRangeFilterProps = {
  resetTableIndex: () => void;
};

const DateRangeFilter = ({ resetTableIndex }: DateRangeFilterProps) => {
  const { handleApplyParams } = useFilterApply();

  const { setDateParams, clearDateRange, dateRange, isDateRangeValid } =
    useBridgeScannerFiltersStore();

  const [isOpen, setIsOpen] = useState(false);

  const { isControlWindowOpen, setControlWindowOpen } = useBridgeScannerStore();

  const toggleFilterWindow = () => {
    setIsOpen((prev) => !prev);
    setControlWindowOpen(!isControlWindowOpen);
  };

  const closeFilterWindow = (event: any) => {
    if ((event?.target as HTMLElement)?.closest(`#dateRange`)) {
      return;
    }
    setIsOpen(false);
    setControlWindowOpen(false);
  };

  const handleClearSelection = () => {
    clearDateRange();
  };

  const setDateRange = (dates: DateRangeFilter) => {
    if (dayjs(dates[0]).isSame(dates[1])) {
      //? Adds the start and end of the day to the date (whole day)
      setDateParams([
        dayjs(dates[0]).startOf("day").toDate(),
        dayjs(dates[0]).endOf("day").toDate(),
      ]);
    } else {
      //? Starts from beginning of the day and ends at the end of the day
      setDateParams([
        dates[0] ? dayjs(dates[0]).startOf("day").toDate() : null,
        dates[1] ? dayjs(dates[1]).endOf("day").toDate() : null,
      ]);
    }
  };

  const handleApply = () => {
    const [fromDate, toDate] = dateRange;
    if ((fromDate && toDate) || (!fromDate && !toDate)) {
      setIsOpen(false);
      setControlWindowOpen(false);
      handleApplyParams();
      resetTableIndex();
    }
  };

  return (
    <div className={styles.popUpWrapper}>
      <button
        className={styles.leftControlBtn}
        id="dateRange"
        onClick={toggleFilterWindow}
      >
        <BiCalendar size={20} />{" "}
        {dateRange.every((v) => v) ? (
          <>
            {dayjs(dateRange[0]).format("DD/MM/YYYY")}
            <FaArrowRightLong size={18} />
            {dayjs(dateRange[1]).format("DD/MM/YYYY")}
          </>
        ) : (
          "Date range"
        )}
      </button>
      {isOpen && (
        <ClickAwayListener onClickAway={closeFilterWindow}>
          <div className={styles.filterPanel}>
            <div className={styles.dateWrapper}>
              <ReactDatePicker
                dateRange={dateRange}
                setDateRange={setDateRange}
              />
              <div className={styles.filterPanelButtons}>
                <button
                  className={styles.resetFilters}
                  onClick={handleClearSelection}
                >
                  Clear selection
                </button>
                <BlessButton
                  disabled={!isDateRangeValid()}
                  onClick={handleApply}
                >
                  Apply
                </BlessButton>
              </div>
            </div>
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
};

export default DateRangeFilter;
